import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { spaceSovereigntyImage } from '@/assets'
import ViewAllButton from '@/components/ui/ViewAllButton'
import { useLanguage } from '@afsa/shared'
import type { WebPageSection } from '@/lib/api'
import { useFilterEvents } from '@/lib/api/hooks/event'
import { usePublishedPressReleases } from '@/lib/api/hooks/press-release'
import { AnimatePresence, motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'
import { parseHtmlString } from '@/utils/helpers/html'
import Heading from '@/components/ui/Typography/Heading'
import { Text } from '@/components/ui/Typography'

interface EventCardProps {
  title: string
  date: string
  location?: string
  image?: string
  featured?: boolean
  onClick?: () => void
}

const EventCard = ({ title, date, image, featured = false, onClick }: EventCardProps) => {
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.src !== spaceSovereigntyImage) {
      e.currentTarget.src = spaceSovereigntyImage
    }
  }

  if (featured) {
    return (
      <motion.article
        className="bg-duskySky-100 h-full overflow-hidden transition-colors duration-300 cursor-pointer hover:bg-gray-50"
        whileHover={{ y: -5 }}
        transition={{ type: 'spring', stiffness: 280, damping: 24 }}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick?.()
          }
        }}
        aria-label={`View event: ${title}`}
      >
        <img
          src={image || spaceSovereigntyImage}
          alt={title}
          className="w-full min-h-[16rem] aspect-video h-max object-cover"
          onError={handleImageError}
        />
        <div className="p-4">
          <Heading level={5} className=" mb-3">
            {title}
          </Heading>
          <Text className="flex items-center font-medium justify-start">
            {date}
          </Text>
        </div>
      </motion.article>
    )
  }

  return (
    <motion.article
      className="bg-white transition-colors duration-200 cursor-pointer hover:bg-gray-50 "
      whileHover={{ x: 4 }}
      transition={{ type: 'spring', stiffness: 280, damping: 24 }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.()
        }
      }}
      aria-label={`View event: ${title}`}
    >
      <div className="flex space-x-4">
        <div className="flex-shrink-0">
          <img
            src={image || spaceSovereigntyImage}
            alt={title}
            className="w-32 h-32 object-cover"
            onError={handleImageError}
          />
        </div>
        <div className="flex-1 min-w-0 space-y-4">
          <Heading level={6} className="">
            {title}
          </Heading>
          <Text className="flex items-center text-sm text-gray-600">
            {date}
          </Text>
        </div>
      </div>
    </motion.article>
  )
}

interface EventsSectionProps {
  className?: string;
  data: {
    [key: string]: WebPageSection
  }
}

const EventsSection = ({ className, data }: EventsSectionProps) => {
  const [activeTab, setActiveTab] = useState<'events' | 'press'>('events')
  const { t } = useLanguage()
  const navigate = useNavigate()

  // Fetch events (filtering by status is handled server-side for published events)
  const { data: eventsData } = useFilterEvents(
    { page: 1, size: 7 },
    {}
  )

  // Fetch press releases - use published press releases to show all, not just landing page ones
  const { data: pressReleasesData, isLoading: isPressReleasesLoading } = usePublishedPressReleases(
    { page: 1, size: 10 },
    { pressRelease: true }
  )

  // Format date helper
  const formatDate = (dateString: string | null) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      const day = date.getDate()
      const month = date.toLocaleDateString('en-US', { month: 'short' })
      const year = date.getFullYear().toString().slice(-2)
      return `${day} ${month} '${year}`
    } catch {
      return ''
    }
  }

  // Helper function to parse HTML content and extract text
  const parseHTMLContent = (html: string | null | undefined): string => {
    if (!html) return ''
    if (typeof document !== 'undefined') {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      return tempDiv.textContent || tempDiv.innerText || ''
    }
    return html.replace(/<[^>]*>/g, '').trim()
  }

  // Transform events data
  const eventsList = useMemo(() => {
    const events = (eventsData?.data?.data || []).filter((event) => event.status === 'PUBLISH' && event.datePublished !== null)
    return events.map((event) => ({
      id: event.eventId,
      title: event.eventTitle,
      date: formatDate(event.dateTime || event.datePublished),
      description: event.description || '',
      category: 'Event',
      image: event.imageUrl || spaceSovereigntyImage
    }))
  }, [eventsData])

  // Transform press releases data
  const pressReleases = useMemo(() => {
    const releases = (pressReleasesData?.data?.data || []).filter((release) => release.datePublished !== null)
    return releases.map((release) => {
      const textContent = parseHTMLContent(release.content)
      const description = textContent
        ? textContent.substring(0, 100) + (textContent.length > 100 ? '...' : '')
        : ''

      return {
        id: release.pressReleaseId,
        title: release.title,
        date: formatDate(release.datePublished),
        description,
        category: 'Articles',
        image: release.coverImage || spaceSovereigntyImage
      }
    })
  }, [pressReleasesData])

  const handleViewAllClick = () => {
    if (activeTab === 'press') {
      navigate('/media/press-release')
    } else {
      navigate('/events')
    }
  }

  const handleCardClick = (id: string, type: 'event' | 'press') => {
    if (type === 'event') {
      navigate(`/events/${id}`)
    } else {
      navigate(`/media/press-release/${id}`)
    }
  }

  const getTabConfig = () => {
    switch (activeTab) {
      case 'events':
        return {
          title: `${data?.HOME_EVENTS_OF_AFSA?.title ?? t('events_of_afsa')}`,
          description: `${data?.HOME_EVENTS_OF_AFSA?.description ?? t('browse_past_events')}`,
          featuredEvent: eventsList[0] ? {
            id: eventsList[0].id,
            title: eventsList[0].title,
            date: eventsList[0].date,
            location: eventsList[0].category,
            description: eventsList[0].description,
            image: eventsList[0].image
          } : null
        }
      case 'press':
        return {
          title: `${data?.HOME_STAY_IN_THE_LOOP_OF_AFSA?.title ?? t('stay_in_loop')}`,
          description: `${data?.HOME_STAY_IN_THE_LOOP_OF_AFSA?.description ?? t('stay_in_loop')}`,
          featuredEvent: pressReleases[0] ? {
            id: pressReleases[0].id,
            title: pressReleases[0].title,
            date: pressReleases[0].date,
            location: "Global",
            description: pressReleases[0].description,
            image: pressReleases[0].image
          } : null
        }
      default:
        return {
          title: t('events_of_afsa'),
          description: t('browse_past_events'),
          featuredEvent: null
        }
    }
  }

  const getCurrentTabEvents = () => {
    switch (activeTab) {
      case 'events':
        return eventsList.slice(1) // Skip first one as it's featured
      case 'press':
        return pressReleases.slice(1) // Skip first one as it's featured
      default:
        return eventsList.slice(1)
    }
  }

  return (
    <section className={`py-16  ${className}`} aria-labelledby="events-title">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className=" mb-5">

          {/* Mobile Tabs */}
          <div className='pb-8 md:hidden flex justify-between'>
            <div>
              <div className="flex items-center">
                <button
                  onClick={() => setActiveTab('events')}
                  className={`py-2 text-sm font-medium font-satoshi border-b-2 transition-all duration-300 ease-in-out ${activeTab === 'events'
                    ? 'text-black border-wine-100 '
                    : 'text-gray-600 border-transparent hover:border-gray-300 hover:text-gray-900'
                    }`}
                >
                  {t('events')}
                </button>
                <button
                  onClick={() => setActiveTab('press')}
                  className={`px-4 py-2 text-sm font-medium font-satoshi border-b-2 transition-all duration-300 ease-in-out ${activeTab === 'press'
                    ? 'text-black border-wine-100 '
                    : 'text-gray-600 border-transparent hover:border-gray-300 hover:text-gray-900'
                    }`}
                >
                  {t('press_release_tab')}
                </button>
              </div>
            </div>
            <div className='md:hidden'>
              <ViewAllButton onClick={handleViewAllClick} />
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <Heading level={3}
              id="events-title"
              className=""
            >
              {getTabConfig().title}
            </Heading>

            {/* Desktop Tab Buttons */}
            <div className="hidden sm:flex items-center">
              <button
                onClick={() => setActiveTab('events')}
                className={`px-4 py-2 text-base font-medium font-satoshi border-b-2 transition-all duration-300 ease-in-out ${activeTab === 'events'
                  ? 'text-black border-wine-100 '
                  : 'text-gray-600 border-transparent hover:border-gray-300 hover:text-gray-900'
                  }`}
              >
                {t('events')}
              </button>
              <button
                onClick={() => setActiveTab('press')}
                className={`px-4 py-2 text-base font-medium font-satoshi border-b-2 transition-all duration-300 ease-in-out ${activeTab === 'press'
                  ? 'text-black border-wine-100 '
                  : 'text-gray-600 border-transparent hover:border-gray-300 hover:text-gray-900'
                  }`}
              >
                {t('press_release_tab')}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between ">
            <p className="text-gray-600 max-w-2xl text-lg lg:w-3/4">
              <span dangerouslySetInnerHTML={parseHtmlString(getTabConfig().description)} />
            </p>
            <div className='hidden md:block'>
              <ViewAllButton onClick={handleViewAllClick} />
            </div>
          </div>
        </div>

        {/* Events Layout */}
        {(() => {
          const tabConfig = getTabConfig()
          const currentEvents = getCurrentTabEvents()
          const isLoading = activeTab === 'events' ? false : isPressReleasesLoading

          if (isLoading) {
            return (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading...</p>
              </div>
            )
          }

          if (!tabConfig.featuredEvent && currentEvents.length === 0) {
            return (
              <div className="text-center py-12">
                <p className="text-gray-500">
                  {activeTab === 'events' ? t('no_items_found') : 'No press releases available'}
                </p>
              </div>
            )
          }

          return (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                className="grid grid-cols-1 lg:grid-cols-3 gap-2"
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                {tabConfig.featuredEvent ? (
                  <motion.div className="lg:col-span-1" variants={fadeUp}>
                    <EventCard
                      {...tabConfig.featuredEvent}
                      featured={true}
                      onClick={() => handleCardClick(tabConfig.featuredEvent!.id, activeTab === 'events' ? 'event' : 'press')}
                    />
                  </motion.div>
                ) : null}

                {/* Current Tab Events Grid */}
                <motion.div
                  className={tabConfig.featuredEvent ? "lg:col-span-2" : "lg:col-span-3"}
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  viewport={viewportReveal}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                    {currentEvents.length > 0 ? (
                      currentEvents.map((event, index) => (
                        <motion.div key={`${activeTab}-${event.id || index}`} variants={fadeUp}>
                          <EventCard
                            title={event.title}
                            date={event.date}
                            image={event.image}
                            onClick={() => handleCardClick(event.id, activeTab === 'events' ? 'event' : 'press')}
                          />
                        </motion.div>
                      ))
                    ) : (
                      <div className="col-span-2 text-center py-8 text-gray-500">
                        {t('no_items_found')}
                      </div>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          )
        })()}
      </div>
    </section>
  )
}

export default EventsSection